import {
  Settlement,
  useCreateSettlementMutation,
  useDeleteSettlementMutation,
  useUpdateSettlementMutation,
} from "@ots-frontend/ots-data";
import {useCallback, useEffect} from "react";
import {notifications} from "@mantine/notifications";
import { ResultTypeFrom } from "@reduxjs/toolkit/query";
import {useTranslation} from "react-i18next";
import {useDebouncedState} from "@mantine/hooks";
import {modals} from "@mantine/modals";
import {Text} from "@mantine/core";

export type UseSettlementMutationProps = {}

export type UseSettlementMutationResult = {
  createSettlement: (settlement: Partial<Settlement>, onSuccess: (() => void) | undefined) => void
  updateSettlement: (settlement: Partial<Settlement>, onSuccess: (() => void) | undefined) => void
  deleteSettlement: (id: string, onSuccess: (() => void) | undefined) => void

  isCreating?: boolean
  isUpdating?: boolean
  isDeleting?: boolean
}

export const useSettlementMutation = (props: UseSettlementMutationProps = {}): UseSettlementMutationResult => {
  const {t} = useTranslation(["common"])

  // state
  const [_creating, _setCreating] = useDebouncedState(false, 500)
  const [_updating, _setUpdating] = useDebouncedState(false, 500)
  const [_deleting, _setDeleting] = useDebouncedState(false, 500)

  // possible mutations
  const [_createSettlement, {isLoading: isCreating}] = useCreateSettlementMutation()
  const [_updateSettlement, {isLoading: isUpdating}] = useUpdateSettlementMutation()
  const [_deleteSettlement, {isLoading: isDeleting}] = useDeleteSettlementMutation()

  useEffect(() => {
    _setCreating(isCreating)
  }, [isCreating, _setCreating])

  useEffect(() => {
    _setUpdating(isUpdating)
  }, [isUpdating, _setUpdating])

  useEffect(() => {
    _setDeleting(isDeleting)
  }, [isDeleting, _setDeleting])

  useEffect(() => {
    if (_creating) {
      notifications.show({
        id: "useSettlementUpdate_creating",
        title: t("common.status.creating", "Creating") + " " + t("common.entity.settlement", "Settlement"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useSettlementUpdate_creating")
    }
  }, [_creating, t]);

  useEffect(() => {
    if (_updating) {
      notifications.show({
        id: "useSettlementUpdate_updating",
        title: t("common.status.updating", "Updating") + " " + t("common.entity.settlement", "Settlement"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useSettlementUpdate_updating")
    }
  }, [_updating, t]);

  useEffect(() => {
    if (_deleting) {
      notifications.show({
        id: "useSettlementUpdate_deleting",
        title: t("common.status.deleting", "Deleting") + " " + t("common.entity.settlement", "Settlement"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useSettlementUpdate_deleting")
    }
  }, [_deleting, t]);

  const handleCreateResult = useCallback((promise: Promise<ResultTypeFrom<any>>, onSuccess: (() => void) | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.settlement", "Settlement") + " " + t("common.status.created", "Created"),
          message: ""
        });
        if (onSuccess) {
          onSuccess()
        }
      })
      .catch((rejected) =>
        notifications.show({
          color: "red",
          title: t("common.error", "Error"),
          message: rejected.data?.detail ?? t("common.error", "Error"),
        })
      );
  }, [t])

  const handleUpdateResult = useCallback((promise: Promise<ResultTypeFrom<any>>, onSuccess: (() => void) | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.settlement", "Settlement") + " " + t("common.status.updated", "Updated"),
          message: ""
        });
        if (onSuccess) {
          onSuccess()
        }
      })
      .catch((rejected) =>
        notifications.show({
          color: "red",
          title: t("common.error", "Error"),
          message: rejected.data?.detail ?? t("common.error", "Error"),
        })
      );
  }, [t])

  const handleDeleteResult = useCallback((promise: Promise<ResultTypeFrom<any>>, onSuccess: (() => void) | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.settlement", "Settlement") + " " + t("common.status.deleted", "Deleted"),
          message: ""
        });

        if (onSuccess) {
          onSuccess()
        }
      })
      .catch((rejected) =>
        notifications.show({
          color: "red",
          title: t("common.error", "Error"),
          message: rejected.data?.detail ?? t("common.error", "Error"),
        })
      );
  }, [t])

  const handleDeleteConfirm = useCallback((id: string, onSuccess: (() => void) | undefined = undefined) => {
    const promise = _deleteSettlement({id: id}).unwrap()
    handleDeleteResult(promise, onSuccess)
  }, [_deleteSettlement, handleDeleteResult])

  const createSettlement = useCallback((settlement: Partial<Settlement>, onSuccess: (() => void) | undefined) => {
    const promise = _createSettlement(settlement).unwrap()
    handleCreateResult(promise, onSuccess)
  }, [_createSettlement, handleCreateResult])

  const updateSettlement = useCallback((settlement: Partial<Settlement>, onSuccess: (() => void) | undefined) => {
    const promise = _updateSettlement(settlement).unwrap()
    handleUpdateResult(promise, onSuccess)
  }, [_updateSettlement, handleUpdateResult])

  const deleteSettlement = useCallback((id: string, onSuccess: (() => void) | undefined) => {
    modals.openConfirmModal({
      title: t("common.action.delete", "Delete") + " " + t("common.entity.settlement", "Settlement"),
      centered: true,
      children: (
        <Text size="sm" p="md">
          {t("common.action.delete.settlement.confirm", "Are you sure you want to delete this settlement?")}
        </Text>
      ),
      labels: {
        confirm: t("common.action.delete", "Delete"),
        cancel: t("common.action.cancel", "Cancel"),
      },
      confirmProps: {color: 'red'},
      onConfirm: () => {
        handleDeleteConfirm(id, onSuccess)
      },
    })
  }, [handleDeleteConfirm, t])

  return {
    createSettlement: createSettlement,
    updateSettlement: updateSettlement,
    deleteSettlement: deleteSettlement,
    isCreating: _creating,
    isUpdating: _updating,
    isDeleting: _deleting,
  }
}
